import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorIcon from '@mui/icons-material/Error';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

class VerificarInconsistenciasButton extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            dialogAberto: false,
            carregando: false,
            inconsistencias: null
        };
    }

    static propTypes = {
        /** Função que verifica as inconsistências e retorna um objeto com os resultados */
        onVerificarInconsistencias: PropTypes.func.isRequired,

        /** Tamanho do botão */
        size: PropTypes.oneOf(['small', 'medium', 'large']),

        /** Variante do botão */
        variant: PropTypes.oneOf(['text', 'outlined', 'contained']),

        /** Cor do botão */
        color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning']),

        /** Store de mensagens (necessária para exibir feedback) */
        mensagemStore: PropTypes.shape({
            abrirMensagemSucesso: PropTypes.func.isRequired,
            abrirMensagemErro: PropTypes.func,
            abrirMensagemAlerta: PropTypes.func
        })
    };

    static defaultProps = {
        size: 'medium',
        variant: 'outlined',
        color: 'primary',
        mensagemStore: null
    };

    handleAbrirDialog = () => {
        this.setState({
            dialogAberto: true,
            carregando: true
        });

        setTimeout(() => {
            try {
                const inconsistencias = this.props.onVerificarInconsistencias() || {
                    magistradosMultiplasDeliberacoes: [],
                    autoresSemVotoProprio: [],
                    autoresComVotoRessalva: [],
                    totalInconsistencias: 0
                };

                this.setState({
                    inconsistencias,
                    carregando: false
                });
            } catch (error) {
                console.error('Erro ao verificar inconsistências:', error);
                this.setState({
                    inconsistencias: {
                        magistradosMultiplasDeliberacoes: [],
                        autoresSemVotoProprio: [],
                        autoresComVotoRessalva: [],
                        totalInconsistencias: 0,
                        erro: error.message
                    },
                    carregando: false
                });
            }
        }, 0);
    };

    handleFecharDialog = () => {
        this.setState({
            dialogAberto: false,
            inconsistencias: null,
            carregando: false
        });
    };

    renderDetalheMultiplasDeliberacoes = (inconsistencia) => {
        if (!inconsistencia?.votos) {
            return null;
        }

        return (
            <Box sx={{ ml: 2, mt: 1 }}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                >
                    <strong>Matéria:</strong> {inconsistencia.materia || 'N/A'}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                    sx={{ mt: 0.5 }}
                >
                    <strong>Votos convergentes em:</strong>
                </Typography>

                <List
                    dense
                    sx={{ py: 0 }}
                >
                    {inconsistencia.votos.map((voto) => (
                        <ListItem
                            key={`${voto.deliberacaoId || 'unknown'}-${voto.magistrado || 'unknown'}`}
                            sx={{ py: 0.5, pl: 2 }}
                        >
                            <ListItemIcon sx={{ minWidth: 30 }}>
                                <DescriptionIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={voto.deliberacao || '?'}
                                secondary={`Autor: ${voto.autor || '?'} | Tipo: ${voto.tipo || '?'}`}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        );
    };

    renderDetalheAutorSemVoto = (inconsistencia) => {
        return (
            <Box sx={{ ml: 2, mt: 1 }}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                >
                    <strong>Matéria:</strong> {inconsistencia.materia}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                    sx={{ mt: 0.5 }}
                >
                    <strong>Deliberação:</strong> {inconsistencia.deliberacao}
                </Typography>
            </Box>
        );
    };

    renderConteudoComInconsistencias = () => {
        const { inconsistencias } = this.state;

        return (
            <>
                {/* Magistrados em múltiplas deliberações */}
                {inconsistencias?.magistradosMultiplasDeliberacoes &&
                    inconsistencias.magistradosMultiplasDeliberacoes.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                            <Alert
                                severity="error"
                                icon={<ErrorIcon />}
                                sx={{ mb: 2 }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    component="div"
                                >
                                    Magistrados votando em múltiplas deliberações
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ mt: 0.5 }}
                                >
                                    Um magistrado não pode acompanhar mais de uma deliberação na mesma matéria.
                                </Typography>
                            </Alert>

                            <List dense>
                                {inconsistencias.magistradosMultiplasDeliberacoes.map((item) => (
                                    <React.Fragment key={`${item.magistrado}-${item.materia}`}>
                                        <ListItem alignItems="flex-start">
                                            <ListItemIcon>
                                                <PersonIcon color="error" />
                                            </ListItemIcon>
                                            <ListItemText
                                                disableTypography
                                                primary={
                                                    <>
                                                        <Typography
                                                            display="inline"
                                                            fontWeight="bold"
                                                        >
                                                            {item.magistrado}
                                                        </Typography>
                                                        <Typography
                                                            display="inline"
                                                            fontWeight="medium"
                                                            fontStyle="italic"
                                                        >
                                                            {` - `}
                                                            {item.totalVotosConvergentes} votos convergentes
                                                        </Typography>
                                                    </>
                                                }
                                                secondary={this.renderDetalheMultiplasDeliberacoes(item)}
                                            />
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>
                    )}

                {/* Autores não votando na própria deliberação */}
                {inconsistencias?.autoresSemVotoProprio && inconsistencias.autoresSemVotoProprio.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Alert
                            severity="warning"
                            icon={<WarningIcon />}
                            sx={{ mb: 2 }}
                        >
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                component="div"
                            >
                                Autores não votando em suas próprias deliberações
                            </Typography>
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{ mt: 0.5 }}
                            >
                                O autor de uma deliberação deve obrigatoriamente acompanhá-la.
                            </Typography>
                        </Alert>

                        <List dense>
                            {inconsistencias.autoresSemVotoProprio.map((item) => (
                                <React.Fragment key={`${item.magistrado}-${item.materia}-${item.deliberacao}`}>
                                    <ListItem alignItems="flex-start">
                                        <ListItemIcon>
                                            <AssignmentIcon color="warning" />
                                        </ListItemIcon>
                                        <ListItemText
                                            disableTypography
                                            primary={
                                                <Typography fontWeight="medium">{item.magistrado} (autor)</Typography>
                                            }
                                            secondary={this.renderDetalheAutorSemVoto(item)}
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Autores votando com ressalva */}
                {inconsistencias?.autoresComVotoRessalva && inconsistencias.autoresComVotoRessalva.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                        <Alert
                            severity="error"
                            icon={<ErrorIcon />}
                            sx={{ mb: 2 }}
                        >
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                component="div"
                            >
                                Autores votando com ressalva em suas próprias deliberações
                            </Typography>
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{ mt: 0.5 }}
                            >
                                O autor de uma deliberação não pode ter ressalvas em relação à sua própria proposta.
                            </Typography>
                        </Alert>

                        <List dense>
                            {inconsistencias.autoresComVotoRessalva.map((item) => (
                                <React.Fragment key={`${item.magistrado}-${item.materia}-${item.deliberacao}`}>
                                    <ListItem alignItems="flex-start">
                                        <ListItemIcon>
                                            {item.isRelator ? (
                                                <PersonIcon color="error" />
                                            ) : (
                                                <AssignmentIcon color="error" />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            disableTypography
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography fontWeight="medium">{item.magistrado}</Typography>
                                                    {item.isRelator && (
                                                        <Chip
                                                            label="Relator"
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ ml: 2, mt: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        component="div"
                                                    >
                                                        <strong>Matéria:</strong> {item.materia}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        component="div"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        <strong>Deliberação:</strong> {item.deliberacao}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="error.main"
                                                        sx={{ mt: 0.5, display: 'block' }}
                                                    >
                                                        ❌ Votando com ressalva (deve ser convergente)
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Resumo */}
                <Alert
                    severity="info"
                    sx={{ mt: 3 }}
                >
                    <Typography
                        variant="body2"
                        component="div"
                    >
                        <strong>Ações recomendadas:</strong>
                        <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                            <li>Para magistrados em múltiplas deliberações: Remover votos convergentes extras</li>
                            <li>Para autores sem voto: Adicionar voto convergente na deliberação do autor</li>
                            <li>Clique no magistrado na tabela para corrigir manualmente</li>
                        </ul>
                    </Typography>
                </Alert>
            </>
        );
    };

    renderConteudoSemInconsistencias = () => {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon
                    sx={{
                        fontSize: 60,
                        color: 'success.main',
                        mb: 2
                    }}
                />
                <Typography
                    variant="h6"
                    color="success.main"
                    gutterBottom
                >
                    ✅ Nenhuma inconsistência encontrada!
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    Todas as regras de votação estão sendo respeitadas.
                </Typography>
            </Box>
        );
    };

    renderConteudoDialog = () => {
        const { carregando, inconsistencias } = this.state;

        if (carregando) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <Typography color="text.secondary">Analisando votação...</Typography>
                </Box>
            );
        }

        if (!inconsistencias) {
            return null;
        }

        const temInconsistencias = inconsistencias.totalInconsistencias > 0;

        return (
            <Box sx={{ mt: 1 }}>
                {temInconsistencias ? this.renderConteudoComInconsistencias() : this.renderConteudoSemInconsistencias()}
            </Box>
        );
    };

    render() {
        const { dialogAberto, inconsistencias } = this.state;
        const { size, variant, color } = this.props;

        return (
            <>
                <Button
                    variant={variant}
                    color={color}
                    size={size}
                    startIcon={<FactCheckIcon />}
                    onClick={this.handleAbrirDialog}
                >
                    Verificar Inconsistências
                </Button>

                <Dialog
                    open={dialogAberto}
                    onClose={this.handleFecharDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            pb: 2
                        }}
                    >
                        <FactCheckIcon color="black" />
                        Verificação de Inconsistências
                        {inconsistencias && (
                            <Chip
                                label={`${inconsistencias.totalInconsistencias} encontradas`}
                                color={inconsistencias.totalInconsistencias > 0 ? 'error' : 'success'}
                                size="small"
                                sx={{ ml: 'auto' }}
                            />
                        )}
                    </DialogTitle>

                    <DialogContent sx={{ pt: 3 }}>{this.renderConteudoDialog()}</DialogContent>

                    <DialogActions sx={{ px: 3, pb: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                        <Button
                            onClick={this.handleFecharDialog}
                            color="primary"
                            variant="contained"
                            endIcon={<CloseIcon />}
                        >
                            Fechar
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}

export default VerificarInconsistenciasButton;
